import { FC, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { ConfigProvider, Spin } from "antd"
import { LoadingOutlined } from "@ant-design/icons"
import { InfiniteData } from "@tanstack/react-query"
import { getTheme } from "Store/MultiDomain/MultiDomain.selector"
import {
  IconPreferencesMenuByType,
  PreferencesMenuTypeEnum,
  TitlePreferencesMenuByType,
} from "Data/interfaces/Preferences/IPreferences"
import {
  IRequestReportEmailSettings,
  ReportPeriodicityEnum
} from "Data/interfaces/Preferences/IReport"
import {
  listActivitiesActions,
  listDisciplinesActions,
  listFoldersAndFilesActions,
  listPlottingActions,
  NotificationActionEnum,
  INotification,
  INotificationInAppObra,
  INotificationInAppObraCount,
  INotificationInAppTopic,
  INotificationInfinityData,
} from "Data/interfaces/Notification/INotification"
import {
  DisciplineNotificationPreferenceEnum,
  FileNotificationPreferenceEnum,
  IPreferencesRequest,
  PlottingNotificationPreferenceEnum,
  TopicNotificationPreferenceEnum
} from "Data/interfaces/Preferences/IPreferencesApi"
import {
  NOTIFICATIONS_COUNT_QUERY_KEY,
  NOTIFICATIONS_QUERY_KEY,
  NOTIFICATIONS_TEMPORARY_QUERY_KEY,
  NOTIFICATIONS_PAGE_QUERY_KEY,
  NOTIFICATIONS_IN_APP_OBRA_COUNT_QUERY_KEY,
  NOTIFICATIONS_IN_APP_OBRA_QUERY_KEY,
  NOTIFICATIONS_IN_APP_TOPIC_QUERY_KEY
} from "Data/keys/QueryKeys"
import { useQueryCreateReportEmail } from "Hooks/report/useQueryCreateReportEmail"
import { useQueryUpdateReportEmail } from "Hooks/report/useQueryUpdateReportEmail"
import { useQueryCreateFilePreference } from "Hooks/preferences/useQueryCreateFilePreference"
import { useQueryCreateTopicPreference } from "Hooks/preferences/useQueryCreateTopicPreference"
import { useQueryCreatePlottingPreference } from "Hooks/preferences/useQueryCreatePlottingPreference"
import { useQueryCreateFilePreferenceEmail } from "Hooks/preferences/useQueryCreateFilePreferenceEmail"
import { useQueryCreateTopicPreferenceEmail } from "Hooks/preferences/useQueryCreateTopicPreferenceEmail"
import { useQueryCreatePlottingPreferenceEmail } from "Hooks/preferences/useQueryCreatePlottingPreferenceEmail"
import { useFeatureFlag } from "Hooks/useFeatureFlag"
import { queryClient } from "Utils/queryClient"
import Button from "Components/UI/Button"
import { Icon } from "Components/UI"
import Modal from "Components/UI/Modal"
import ActiveItems from "./components/ActiveItems"
import useOptions from "./useOptions"
import {
  Container,
  Content,
  Footer,
  Left,
  Main,
  MainContent,
  MainContentTop,
  LeftTitle,
} from "./styles"

interface IPreferencesModal {
  visible: boolean
  onCancel: () => void
}

const PreferencesModal: FC<IPreferencesModal> = ({
  visible,
  onCancel,
}) => {
  const { t } = useTranslation(['notification', 'common'])
  const preferencesFeatureFlag = useFeatureFlag('preferences-feature');
  const preferencesFeature = preferencesFeatureFlag.enabled && preferencesFeatureFlag.value === 'test';

  const theme = useSelector(getTheme)
  const {
    hasReportEmailData,
    preferencesEmailData,
    preferencesNotificationsData,
    objOptions,
    arrayOptions,
    statesIsEquals,
    onStateUpdate,
    onOldData
  } = useOptions()
  const {
    createReportEmailAsync,
    isLoading: isLoadingCreateReportEmail
  } = useQueryCreateReportEmail()
  const {
    updateReportEmailAsync,
    isLoading: isLoadingUpdateReportEmail
  } = useQueryUpdateReportEmail()
  const {
    createFilePreferenceAsync,
    isLoading: isLoadingCreateFilePreference
  } = useQueryCreateFilePreference()
  const {
    createTopicPreferenceAsync,
    isLoading: isLoadingCreateTopicPreference
  } = useQueryCreateTopicPreference()
  const {
    createPlottingPreferenceAsync,
    isLoading: isLoadingCreatePlottingPreference
  } = useQueryCreatePlottingPreference()
  const {
    createFilePreferenceEmailAsync,
    isLoading: isLoadingCreateFilePreferenceEmail
  } = useQueryCreateFilePreferenceEmail()
  const {
    createTopicPreferenceEmailAsync,
    isLoading: isLoadingCreateTopicPreferenceEmail
  } = useQueryCreateTopicPreferenceEmail()
  const {
    createPlottingPreferenceEmailAsync,
    isLoading: isLoadingCreatePlottingPreferenceEmail
  } = useQueryCreatePlottingPreferenceEmail()

  const [active, setActive] = useState<PreferencesMenuTypeEnum>(PreferencesMenuTypeEnum.ReportByEmail)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleActive = (activeType: PreferencesMenuTypeEnum) => {
    setActive(activeType)
    onOldData()
  }

  const syncNotificationsWithDatabase = async () => {
    await Promise.all([
      queryClient.invalidateQueries<InfiniteData<INotificationInfinityData>>(
        { queryKey: [NOTIFICATIONS_QUERY_KEY] }
      ),
      queryClient.invalidateQueries<INotification[]>(
        { queryKey: [NOTIFICATIONS_TEMPORARY_QUERY_KEY] }
      ),
      queryClient.invalidateQueries<number>(
        { queryKey: [NOTIFICATIONS_COUNT_QUERY_KEY] }
      ),
      queryClient.invalidateQueries<INotificationInAppObraCount[]>(
        { queryKey: [NOTIFICATIONS_IN_APP_OBRA_COUNT_QUERY_KEY] }
      ),
      queryClient.invalidateQueries<INotificationInAppObra[]>(
        { queryKey: [NOTIFICATIONS_IN_APP_OBRA_QUERY_KEY] }
      ),
      queryClient.invalidateQueries<INotificationInAppTopic[]>(
        { queryKey: [NOTIFICATIONS_IN_APP_TOPIC_QUERY_KEY] }
      ),
      queryClient.invalidateQueries<InfiniteData<INotificationInfinityData>>(
        { queryKey: [NOTIFICATIONS_PAGE_QUERY_KEY] }
      )
    ]);
  }

  const handleSubmit = async (type: PreferencesMenuTypeEnum) => {
    setIsSubmitting(true)
    if (type === PreferencesMenuTypeEnum.ReportByEmail) {
      const data = {
        receiveFileReportEmail: false,
        fileReportPeriodicity: ReportPeriodicityEnum.Weekly,
        receiveTopicReportEmail: false,
        topicReportPeriodicity: ReportPeriodicityEnum.Weekly,
        receivePlottReportEmail: false,
        plottReportPeriodicity: ReportPeriodicityEnum.Weekly,
      } as IRequestReportEmailSettings;
      objOptions[type].Items.forEach((itemParent) => {
        itemParent.Items.forEach((item) => {
          item.Items.forEach((itemChildren) => {
            if (
              itemChildren.ParamRequestSwitch &&
              itemChildren.ParamRequestSwitch in data
            ) {
              (data as any)[itemChildren.ParamRequestSwitch] = itemChildren.ValueSwitch as boolean
            }
            if (
              itemChildren.ParamRequestSelect &&
              itemChildren.ParamRequestSelect in data
            ) {
              (data as any)[itemChildren.ParamRequestSelect] = ReportPeriodicityEnum[itemChildren.ValueSelect as any]
            }
          })
        })
      })
      if (hasReportEmailData) {
        await updateReportEmailAsync(data)
      } else {
        await createReportEmailAsync(data)
      }
    }
    if (type === PreferencesMenuTypeEnum.Notifications) {
      const fileData: IPreferencesRequest[] = []
      const activityData: IPreferencesRequest[] = []
      const disciplineData: IPreferencesRequest[] = []
      const plottingData: IPreferencesRequest[] = []
      objOptions[type].Items.forEach((itemParent) => {
        itemParent.Items.forEach((item) => {
          item.Items.forEach((itemChildren) => {
            if (preferencesFeature) {
              const preferenceNotificationsItem = preferencesNotificationsData?.find(item => {
                return item.Action === itemChildren.SelectType as number
              })
              if (preferenceNotificationsItem) {
                const preferenceValue = preferenceNotificationsItem?.Preference as number
                if (listFoldersAndFilesActions.includes(itemChildren.SelectType as NotificationActionEnum)) {
                  const valueSwitchFile = itemChildren?.ValueSwitch
                    ? FileNotificationPreferenceEnum.AllFolders
                    : FileNotificationPreferenceEnum.None
                  if (preferenceValue !== valueSwitchFile) {
                    fileData.push({
                      action: itemChildren.SelectType as NotificationActionEnum,
                      preference: valueSwitchFile
                    })
                  }
                }
                if (listActivitiesActions.includes(itemChildren.SelectType as NotificationActionEnum)) {
                  const valueTopic = itemChildren?.ValueSwitch
                    ? TopicNotificationPreferenceEnum[itemChildren.ValueSelect as keyof typeof TopicNotificationPreferenceEnum] as number
                    : TopicNotificationPreferenceEnum.None
                  if (preferenceValue !== valueTopic) {
                    activityData.push({
                      action: itemChildren.SelectType as NotificationActionEnum,
                      preference: valueTopic
                    })
                  }
                }
                if (listDisciplinesActions.includes(itemChildren.SelectType as NotificationActionEnum)) {
                  const valueSwitchDiscipline = itemChildren?.ValueSwitch
                    ? DisciplineNotificationPreferenceEnum.All
                    : DisciplineNotificationPreferenceEnum.None
                  if (preferenceValue !== valueSwitchDiscipline) {
                    disciplineData.push({
                      action: itemChildren.SelectType as NotificationActionEnum,
                      preference: valueSwitchDiscipline
                    })
                  }
                }
                if (listPlottingActions.includes(itemChildren.SelectType as NotificationActionEnum)) {
                  const valuePlotting = itemChildren?.ValueSwitch
                    ? PlottingNotificationPreferenceEnum[itemChildren.ValueSelect as keyof typeof PlottingNotificationPreferenceEnum] as number
                    : PlottingNotificationPreferenceEnum.None
                  if (preferenceValue !== valuePlotting) {
                    plottingData.push({
                      action: itemChildren.SelectType as NotificationActionEnum,
                      preference: valuePlotting
                    })
                  }
                }
              }
            }
          })
        })
      })
      if (fileData.length > 0) {
        await Promise.all(fileData.map(item => createFilePreferenceAsync(item)))
      }
      if (disciplineData.length > 0) {
        await Promise.all(disciplineData.map(item => createFilePreferenceAsync(item)))
      }
      if (activityData.length > 0) {
        await Promise.all(activityData.map(item => createTopicPreferenceAsync(item)))
      }
      if (plottingData.length > 0) {
        await Promise.all(plottingData.map(item => createPlottingPreferenceAsync(item)))
      }
      await syncNotificationsWithDatabase()
    }
    if (type === PreferencesMenuTypeEnum.NotificationByEmail) {
      const fileData: IPreferencesRequest[] = []
      const activityData: IPreferencesRequest[] = []
      const disciplineData: IPreferencesRequest[] = []
      const plottingData: IPreferencesRequest[] = []
      objOptions[type].Items.forEach((itemParent) => {
        itemParent.Items.forEach((item) => {
          item.Items.forEach((itemChildren) => {
            if (preferencesFeature) {
              const preferenceEmailItem = preferencesEmailData?.find(item => {
                return item.Action === itemChildren.SelectType as number
              })
              if (preferenceEmailItem) {
                const preferenceValue = preferenceEmailItem?.Preference as number
                if (listFoldersAndFilesActions.includes(itemChildren.SelectType as NotificationActionEnum)) {
                  const valueSwitchFile = itemChildren?.ValueSwitch
                    ? FileNotificationPreferenceEnum.AllFolders
                    : FileNotificationPreferenceEnum.None
                  if (preferenceValue !== valueSwitchFile) {
                    fileData.push({
                      action: itemChildren.SelectType as NotificationActionEnum,
                      preference: valueSwitchFile
                    })
                  }
                }
                if (listActivitiesActions.includes(itemChildren.SelectType as NotificationActionEnum)) {
                  const valueTopic = itemChildren?.ValueSwitch
                    ? TopicNotificationPreferenceEnum[itemChildren.ValueSelect as keyof typeof TopicNotificationPreferenceEnum] as number
                    : TopicNotificationPreferenceEnum.None
                  if (preferenceValue !== valueTopic) {
                    activityData.push({
                      action: itemChildren.SelectType as NotificationActionEnum,
                      preference: valueTopic
                    })
                  }
                }
                if (listDisciplinesActions.includes(itemChildren.SelectType as NotificationActionEnum)) {
                  const valueSwitchDiscipline = itemChildren?.ValueSwitch
                    ? DisciplineNotificationPreferenceEnum.All
                    : DisciplineNotificationPreferenceEnum.None
                  if (preferenceValue !== valueSwitchDiscipline) {
                    disciplineData.push({
                      action: itemChildren.SelectType as NotificationActionEnum,
                      preference: valueSwitchDiscipline
                    })
                  }
                }
                if (listPlottingActions.includes(itemChildren.SelectType as NotificationActionEnum)) {
                  const valuePlotting = itemChildren?.ValueSwitch
                    ? PlottingNotificationPreferenceEnum[itemChildren.ValueSelect as keyof typeof PlottingNotificationPreferenceEnum] as number
                    : PlottingNotificationPreferenceEnum.None
                  if (preferenceValue !== valuePlotting) {
                    plottingData.push({
                      action: itemChildren.SelectType as NotificationActionEnum,
                      preference: valuePlotting
                    })
                  }
                }
              }
            }
          })
        })
      })
      if (fileData.length > 0) {
        await Promise.all(fileData.map(item => createFilePreferenceEmailAsync(item)))
      }
      if (disciplineData.length > 0) {
        await Promise.all(disciplineData.map(item => createFilePreferenceEmailAsync(item)))
      }
      if (activityData.length > 0) {
        await Promise.all(activityData.map(item => createTopicPreferenceEmailAsync(item)))
      }
      if (plottingData.length > 0) {
        await Promise.all(plottingData.map(item => createPlottingPreferenceEmailAsync(item)))
      }
    }
    setIsSubmitting(false)
  }

  const updateLoading = isLoadingCreateReportEmail ||
    isLoadingUpdateReportEmail ||
    isLoadingCreateFilePreference ||
    isLoadingCreateFilePreferenceEmail ||
    isLoadingCreateTopicPreference ||
    isLoadingCreatePlottingPreference ||
    isLoadingCreateTopicPreferenceEmail ||
    isLoadingCreatePlottingPreferenceEmail

  useEffect(() => {
    if (isSubmitting && !updateLoading) {
      setIsSubmitting(false)
    }
  }, [isSubmitting, updateLoading])

  return (
    <>
      <Modal
        visible={visible}
        width={750}
        footerButtons={null}
        onCancel={onCancel}
      >
        <Container>
          <Content>
            <Left>
              <span className="textTitle">{t('notification:preferences.modal.title')}</span>
              {arrayOptions.map((opt) => {
                const isActive = opt.Type === active

                return (
                  <LeftTitle
                    key={opt.Id}
                    isactive={isActive}
                    isdisabled={opt.IsDisabled}
                    onClick={() => !opt.IsDisabled && handleActive(opt.Type)}
                  >
                    <Icon
                      icon={IconPreferencesMenuByType[opt.Type]}
                      className="iconTitle"
                    />
                    <span className="title">
                      {TitlePreferencesMenuByType[opt.Type]}
                    </span>
                    {opt.IsDisabled && (
                      <span className="badgeUpdate">{t('notification:preferences.modal.comingSoon')}</span>
                    )}
                  </LeftTitle>
                )
              })}
            </Left>

            <Main>
              <MainContent>
                <ConfigProvider theme={{
                  token: {
                    colorBgContainerDisabled: `color-mix(in srgb, ${theme.colors.primary.primaryOutline}, ${theme.colors.surface.surface})`,
                    colorTextDisabled: theme.colors.primary.onPrimary,
                    colorPrimary: theme.colors.primary.primary
                  }
                }}>
                  <MainContentTop hasfooter={!statesIsEquals || isSubmitting}>
                    <ActiveItems
                      active={objOptions[active]}
                      onStateUpdate={onStateUpdate}
                    />
                  </MainContentTop>
                </ConfigProvider>
              </MainContent>
              {(!statesIsEquals || isSubmitting) && (
                <Footer>
                  <Button
                    type="primary"
                    className="btnFooter"
                    disabled={isSubmitting || updateLoading}
                    onClick={() => handleSubmit(active)}
                  >
                    {updateLoading && (
                      <Spin
                        indicator={
                          <LoadingOutlined
                            color="white"
                            style={{ fontSize: 12, color: 'white', marginRight: 6 }}
                          />
                        }
                      />
                    )}
                    {updateLoading ? t('common:saving') : t('common:save')}
                  </Button>
                </Footer>
              )}
            </Main>
          </Content>
        </Container>
      </Modal>
    </>
  )
}

export default PreferencesModal
